import { FactoryTypes, FieldID, GuideTypes } from "../models";
import { FlowTypes, MobileDeviceValues } from "@trailhead/smart-setup-core";
import {
  androidToSamsungSetupLostPhone,
  androidToSamsungSetupWithPhone,
  backupAndroidContent,
  backupIphone,
  iphoneSetupContentLostPhone,
  iphoneSetupContentWithPhone,
  iphoneToIphoneSetupWithPhone,
  iphoneToSamsungSetupWithPhone,
  samsungToAndroidWithPhone,
  iphoneToAndroidSetupWithPhone,
  iphoneToSamsungSetupLostPhone,
  iphoneToAndroidSetupLostPhone,
  iphoneSendBackContent,
  sendBackContent,
  samsungToAndroidLostPhone,
  iphone14SetupContentWithPhone,
  iphone14SetupContentLostPhone,
  eSIMiPhone14GenericActivation,
} from "./common";

const activationContent = (newPhone: MobileDeviceValues) => ({
  criteria: {
    newPhone,
    newDeviceActivated: false,
    deviceFlowType: FlowTypes.Replacement,
  },
  content: [
    {
      type: FactoryTypes.GUIDE_INTRO,
      fieldMapping: {
        text: FieldID.guideContainer.introduction.text,
      },
    },
    {
      type: FactoryTypes.DECISION_STEP,
      fieldMapping: {
        questionText: FieldID.guideContainer.decisionStep.decisionOldSimCard,
        yesSteps: [
          {
            type: FactoryTypes.POPUP,
            fieldMapping: {
              buttonText:
                FieldID.guideContainer.simCardExplanation.questionText,
              popupTitleText:
                FieldID.guideContainer.simCardExplanation.questionText,
              popupDescriptionText:
                FieldID.guideContainer.simCardExplanation.descriptionText,
              popupImageDescriptionText:
                FieldID.guideContainer.simCardExplanation.imageDescription,
              popupImage: FieldID.guideContainer.simCardExplanation.image,
            },
          },
          {
            type: FactoryTypes.NUMBERED_STEP,
            fieldMapping: {
              text: FieldID.guideContainer.steps.activateBothPhonesPoweredOff
                .text,
            },
          },
          {
            type: FactoryTypes.NUMBERED_STEP,
            fieldMapping: {
              text: FieldID.guideContainer.steps.locateSimCardTrayStep.text,
              image: FieldID.guideContainer.steps.locateSimCardTrayStep.image,
              imageHeader:
                FieldID.guideContainer.steps.locateSimCardTrayStep.imageHeader,
            },
          },
          {
            type: FactoryTypes.NUMBERED_STEP,
            fieldMapping: {
              text: FieldID.guideContainer.steps.activateRemoveSimTray.text,
              image: FieldID.guideContainer.steps.activateRemoveSimTray.image,
            },
          },
          {
            type: FactoryTypes.NUMBERED_STEP,
            fieldMapping: {
              text: FieldID.guideContainer.steps.activateRepeatRemoveSimProcess
                .text,
            },
          },
          {
            type: FactoryTypes.TIPBOX,
            fieldMapping: {
              text: FieldID.guideContainer.tipBox.simCardCautionText,
              icon: FieldID.guideContainer.tipBox.iconV2,
            },
          },
          {
            type: FactoryTypes.STEP,
            fieldMapping: {
              image: FieldID.guideContainer.steps.insertNewSimCard.image,
            },
          },
          {
            type: FactoryTypes.EXPERT_HELP,
            fieldMapping: {
              buttonText: FieldID.guideContainer.expertHelpContent.buttonText,
              title: FieldID.guideContainer.expertHelpContent.title,
              subtext: FieldID.guideContainer.expertHelpContent.subtext,
              secondaryButtonText:
                FieldID.guideContainer.expertHelpContent.secondaryButtonText,
              supportNumber:
                FieldID.guideContainer.expertHelpContent.supportNumber,
            },
          },
          {
            type: FactoryTypes.NUMBERED_STEP,
            fieldMapping: {
              text: FieldID.guideContainer.steps.activateTurnOnNewPhone.text,
            },
          },
        ],
        noSteps: [
          {
            type: FactoryTypes.POPUP,
            fieldMapping: {
              buttonText:
                FieldID.guideContainer.simCardExplanation.questionText,
              popupTitleText:
                FieldID.guideContainer.simCardExplanation.questionText,
              popupDescriptionText:
                FieldID.guideContainer.simCardExplanation.descriptionText,
              popupImageDescriptionText:
                FieldID.guideContainer.simCardExplanation.imageDescription,
              popupImage: FieldID.guideContainer.simCardExplanation.image,
            },
          },
          {
            type: FactoryTypes.NUMBERED_STEP,
            fieldMapping: {
              text: FieldID.guideContainer.steps.powerOffNewPhoneStep.text,
            },
          },
          {
            type: FactoryTypes.NUMBERED_STEP,
            fieldMapping: {
              text: FieldID.guideContainer.steps.locateSimCardTrayStep.text,
              image: FieldID.guideContainer.steps.locateSimCardTrayStep.image,
              imageHeader:
                FieldID.guideContainer.steps.locateSimCardTrayStep.imageHeader,
            },
          },
          {
            type: FactoryTypes.NUMBERED_STEP,
            fieldMapping: {
              text: FieldID.guideContainer.steps.insertNewSimCard.text,
              image: FieldID.guideContainer.steps.insertNewSimCard.image,
            },
          },
          {
            type: FactoryTypes.TIPBOX,
            fieldMapping: {
              text: FieldID.guideContainer.tipBox.simCardCautionText,
              icon: FieldID.guideContainer.tipBox.iconV2,
            },
          },
          {
            type: FactoryTypes.NUMBERED_STEP,
            fieldMapping: {
              text: FieldID.guideContainer.steps.phoneActivation.text,
              substep: FieldID.guideContainer.steps.phoneActivation.substep,
            },
          },
          {
            type: FactoryTypes.EXPERT_HELP,
            fieldMapping: {
              buttonText: FieldID.guideContainer.expertHelpContent.buttonText,
              title: FieldID.guideContainer.expertHelpContent.title,
              subtext: FieldID.guideContainer.expertHelpContent.subtext,
              secondaryButtonText:
                FieldID.guideContainer.expertHelpContent.secondaryButtonText,
              supportNumber:
                FieldID.guideContainer.expertHelpContent.supportNumber,
            },
          },
          {
            type: FactoryTypes.NUMBERED_STEP,
            fieldMapping: {
              text: FieldID.guideContainer.steps.activateTurnOnNewPhone.text,
            },
          },
        ],
      },
    },
  ],
});

export const LibertyContent = {
  [GuideTypes.BACKUP]: [
    {
      criteria: {
        oldPhone: MobileDeviceValues.iPhone,
      },
      content: backupIphone,
    },
    backupAndroidContent(MobileDeviceValues.Samsung),
    backupAndroidContent(MobileDeviceValues.OtherAndroid),
    backupAndroidContent(MobileDeviceValues.Pixel),
  ],
  [GuideTypes.ACTIVATE]: [
    eSIMiPhone14GenericActivation,
    activationContent(MobileDeviceValues.iPhone),
    activationContent(MobileDeviceValues.Samsung),
    activationContent(MobileDeviceValues.OtherAndroid),
    activationContent(MobileDeviceValues.Pixel),
  ],

  [GuideTypes.SETUP]: [
    {
      criteria: {
        oldPhone: MobileDeviceValues.iPhone,
        newPhone: MobileDeviceValues.iPhone,
        oldDeviceFunctional: true,
      },
      content: iphoneToIphoneSetupWithPhone,
    }, //iphone to iphone with phone
    {
      criteria: {
        oldPhone: MobileDeviceValues.iPhone,
        newPhone: MobileDeviceValues.Samsung,
        oldDeviceFunctional: true,
      },
      content: iphoneToSamsungSetupWithPhone,
    }, //iphone to samsung with phone
    {
      criteria: {
        oldPhone: MobileDeviceValues.iPhone,
        newPhone: MobileDeviceValues.Samsung,
        oldDeviceFunctional: false,
      },
      content: iphoneToSamsungSetupLostPhone,
    }, //iphone to samsung lost phone
    {
      criteria: {
        oldPhone: MobileDeviceValues.iPhone,
        newPhone: MobileDeviceValues.OtherAndroid,
        oldDeviceFunctional: true,
      },
      content: iphoneToAndroidSetupWithPhone,
    }, //iphone to android with phone
    {
      criteria: {
        oldPhone: MobileDeviceValues.iPhone,
        newPhone: MobileDeviceValues.OtherAndroid,
        oldDeviceFunctional: false,
      },
      content: iphoneToAndroidSetupLostPhone,
    }, //iphone to android lost phone

    {
      criteria: {
        newPhone: MobileDeviceValues.iPhone,
        oldDeviceFunctional: true,
        newDeviceIsiPhone14: true,
        deviceFlowType: FlowTypes.NewDevice,
      },
      criteriaWeight: {
        newDeviceIsiPhone14: 5,
      },
      content: iphone14SetupContentWithPhone(FlowTypes.NewDevice),
    }, //iphone14 setup guide

    {
      criteria: {
        newPhone: MobileDeviceValues.iPhone,
        oldDeviceFunctional: true,
        newDeviceIsiPhone14: true,
        deviceFlowType: FlowTypes.Replacement,
      },
      criteriaWeight: {
        newDeviceIsiPhone14: 5,
      },
      content: iphone14SetupContentWithPhone(FlowTypes.Replacement),
    }, //iphone14 setup guide

    {
      criteria: {
        newPhone: MobileDeviceValues.iPhone,
        oldDeviceFunctional: false,
        newDeviceIsiPhone14: true,
        deviceFlowType: FlowTypes.NewDevice,
      },
      criteriaWeight: {
        newDeviceIsiPhone14: 5,
      },
      content: iphone14SetupContentLostPhone(FlowTypes.NewDevice),
    }, //iphone14 setup guide without phone

    {
      criteria: {
        newPhone: MobileDeviceValues.iPhone,
        oldDeviceFunctional: false,
        newDeviceIsiPhone14: true,
        deviceFlowType: FlowTypes.Replacement,
      },
      criteriaWeight: {
        newDeviceIsiPhone14: 5,
      },
      content: iphone14SetupContentLostPhone(FlowTypes.Replacement),
    }, //iphone14 setup guide without phone
    //--------------------------------------------

    {
      criteria: {
        oldPhone: MobileDeviceValues.Samsung,
        newPhone: MobileDeviceValues.Samsung,
        oldDeviceFunctional: true,
      },
      content: androidToSamsungSetupWithPhone,
    }, //samsung to samsung with phone
    {
      criteria: {
        newPhone: MobileDeviceValues.Samsung,
        oldDeviceFunctional: false,
      },
      content: androidToSamsungSetupLostPhone,
    }, //samsung without phone
    {
      criteria: {
        oldPhone: MobileDeviceValues.Samsung,
        newPhone: MobileDeviceValues.iPhone,
        oldDeviceFunctional: true,
      },
      content: iphoneSetupContentWithPhone,
    }, //samsung to iphone with phone
    {
      criteria: {
        oldPhone: MobileDeviceValues.Samsung,
        newPhone: MobileDeviceValues.OtherAndroid,
        oldDeviceFunctional: true,
      },
      content: samsungToAndroidWithPhone,
    }, //samsung to android with phone
    {
      criteria: {
        newPhone: MobileDeviceValues.OtherAndroid,
        oldDeviceFunctional: false,
      },
      content: samsungToAndroidLostPhone,
    }, //android without phone
    {
      criteria: {
        oldPhone: MobileDeviceValues.OtherAndroid,
        newPhone: MobileDeviceValues.iPhone,
        oldDeviceFunctional: true,
      },
      content: iphoneSetupContentWithPhone,
    }, //android to iphone with phone
    {
      criteria: {
        oldPhone: MobileDeviceValues.OtherAndroid,
        newPhone: MobileDeviceValues.Samsung,
        oldDeviceFunctional: true,
      },
      content: androidToSamsungSetupWithPhone,
    }, //android to samsung with phone
    {
      criteria: {
        oldPhone: MobileDeviceValues.OtherAndroid,
        newPhone: MobileDeviceValues.OtherAndroid,
        oldDeviceFunctional: true,
      },
      content: samsungToAndroidWithPhone,
    }, //android to android with phone

    {
      criteria: {
        newPhone: MobileDeviceValues.iPhone,
        oldDeviceFunctional: false,
      },
      content: iphoneSetupContentLostPhone,
    }, //x to iphone
    {
      criteria: {
        oldPhone: MobileDeviceValues.OtherAndroid,
        newPhone: MobileDeviceValues.Samsung,
        oldDeviceFunctional: false,
      },
      content: androidToSamsungSetupLostPhone,
    }, //android to samsung WITHOUT phone
    {
      criteria: {
        oldPhone: MobileDeviceValues.Samsung,
        newPhone: MobileDeviceValues.Samsung,
        oldDeviceFunctional: false,
      },
      content: androidToSamsungSetupLostPhone,
    }, //samsung to samsung WITHOUT phone
    {
      criteria: {
        oldPhone: MobileDeviceValues.Samsung,
        newPhone: MobileDeviceValues.OtherAndroid,
        oldDeviceFunctional: false,
      },
      content: androidToSamsungSetupLostPhone,
    }, //samsung to android WITHOUT phone
    {
      criteria: {
        oldPhone: MobileDeviceValues.OtherAndroid,
        newPhone: MobileDeviceValues.OtherAndroid,
        oldDeviceFunctional: false,
      },
      content: androidToSamsungSetupLostPhone,
    }, //android to android WITHOUT phone
  ],
  [GuideTypes.SEND_BACK]: [
    iphoneSendBackContent,
    sendBackContent(MobileDeviceValues.Samsung),
    sendBackContent(MobileDeviceValues.OtherAndroid),
    sendBackContent(MobileDeviceValues.Pixel),
  ],
};
